import type { ReadValuationReportMode } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { getAccessToken } from '@/lib/auth/access-token-store';

/**
 * Fetch a valuation's HTML report as markup for the operator's sandboxed viewer
 * (IMM-598) — the admin-tier twin of the customer app's helper. The route is a
 * `stream` leaf, so the typed client resolves it to a URL rather than issuing the
 * request; we fetch it ourselves with the bearer token. Returns `null` when the
 * report can no longer be read (not readable yet, out of scope, gone), which the
 * report block surfaces as its error state.
 *
 * Same two accepted limitations as the customer helper, both degrading to that
 * error state rather than to anything unsafe: no 401 refresh (the fetch goes
 * around `apiClient`, so a viewer mounted on a just-expired token shows the error
 * and a reload is the recovery), and `response.text()` decodes as UTF-8 whatever
 * the report's own encoding is.
 */
export async function fetchValuationReportHtml(valuationRequestId: string): Promise<string | null> {
  const response = await requestValuationReportHtml(valuationRequestId);

  return response.ok ? await response.text() : null;
}

/**
 * Fallback download name when the report carries no `originalFilename`. The `.html`
 * extension is what names the saved file as the report it is — the same constant
 * the customer app uses.
 */
const HTML_REPORT_FALLBACK_FILENAME = 'wertgutachten.html';

/**
 * Save a valuation's HTML report to disk under its original filename (IMM-641.1) —
 * the operator-tier twin of the customer app's helper shipped by IMM-578. The report
 * bytes are read with `response.blob()` — deliberately NOT `.text()`, which
 * {@link fetchValuationReportHtml} uses to feed the sandboxed viewer: `.text()`
 * decodes as UTF-8 and would write a Windows-1252 Wertgutachten to disk with
 * mojibake. Saving a wrong byte is permanent where rendering one is not, so the two
 * paths keep different body reads rather than sharing a decode flag. Returns `false`
 * on a non-ok response so the report block surfaces its inline error.
 */
export async function downloadValuationReportHtml(
  valuationRequestId: string,
  originalFilename: string | null,
): Promise<boolean> {
  const response = await requestValuationReportHtml(valuationRequestId);

  if (!response.ok) return false;

  const blob = await response.blob();
  // The browser takes the saved name from the anchor's `download` attribute and
  // ignores any server content-disposition for a blob URL — which is exactly why
  // this needs no backend change (see the slice's ## Decisions).
  const objectUrl = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = originalFilename ?? HTML_REPORT_FALLBACK_FILENAME;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // `createObjectURL` pins the blob in memory until revoked, and a Wertgutachten
    // may reach the report size cap.
    URL.revokeObjectURL(objectUrl);
  }

  return true;
}

/**
 * Issue the bearer-authenticated GET for a valuation's HTML report stream, shared by
 * the preview fetch and the download so their auth shape and client header can never
 * drift. Each caller decodes the returned body itself — the preview as text for the
 * viewer, the download as a blob for byte fidelity — which is deliberately not a
 * decode flag on one function.
 */
async function requestValuationReportHtml(valuationRequestId: string): Promise<Response> {
  const url = apiClient.adminValuationRequests.readReportHtml({ pathParams: { id: valuationRequestId } });
  const token = getAccessToken();

  return fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      // The admin-tier value — 'web' would be refused at the edge.
      'X-Immo-Client': 'admin',
      ...(token !== null ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

/**
 * Mint a short-lived presigned GET URL for a request's held `DOCUMENT` report on
 * the operator surface. `preview` yields an inline URL for an in-app Vorschau,
 * `download` yields an attachment URL the browser saves under the original
 * filename. The URL is minted per call and never stored. Returns the URL on ok, or
 * `null` when the report can no longer be read.
 */
export async function resolveValuationReportUrl(
  valuationRequestId: string,
  mode: ReadValuationReportMode,
): Promise<string | null> {
  const result = await apiClient.adminValuationRequests.readReport({
    pathParams: { id: valuationRequestId },
    query: { mode },
  });

  return result.ok ? result.body.url : null;
}

/**
 * Open the valuation report inline in a new tab (Vorschau). Returns `true` on
 * success, or `false` when the URL can no longer be minted so the caller can
 * surface an error.
 */
export async function previewValuationReport(valuationRequestId: string): Promise<boolean> {
  const url = await resolveValuationReportUrl(valuationRequestId, 'preview');

  if (url === null) return false;

  window.open(url, '_blank', 'noopener');

  return true;
}

/**
 * Trigger a byte-for-byte download of the valuation report under its original
 * filename. Clicks a programmatic anchor — the S3 target carries an `attachment`
 * content-disposition, so we deliberately do NOT set the `download` attribute
 * (ignored cross-origin) nor blob-fetch (CORS). Returns `true` on success, or
 * `false` when the URL can no longer be minted.
 */
export async function downloadValuationReport(valuationRequestId: string): Promise<boolean> {
  const url = await resolveValuationReportUrl(valuationRequestId, 'download');

  if (url === null) return false;

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noopener';
  anchor.click();

  return true;
}
